import { StyledEngineProvider, ThemeProvider } from '@mui/material/styles'
import { GroupChannel, GroupChannelHandler, Member } from '@sendbird/chat/groupChannel'
import { OpenChannelHandler } from '@sendbird/chat/openChannel'
import Cookies from 'js-cookie'
import { SnackbarProvider } from 'notistack'
import { useEffect, useMemo, useState } from 'react'
import { Provider } from 'react-redux'
import { BrowserRouter } from 'react-router-dom'

import 'App.css'
import { SnackbarUtilsConfigurator } from 'components/Snackbar'
import { useThemeService } from 'components/Theme/hooks/useThemeService'
import Env from 'config/Env'
import { DarkColors } from 'constants/DarkColors'
import { LightColors } from 'constants/LightColors'
import { useLoginService } from 'features/account/hooks'
import { RoleObject } from 'features/account/types'
import { useDiscussionService } from 'features/discussions/hooks'
import { useNotificationsService } from 'features/notifications/hooks'
import { useReferralsService } from 'features/referrals/hooks'
import { TaskListRequest, TodoListResponse } from 'features/referrals/types'
import usePendoPageTracking from 'hooks/usePendoTracking'
import AppRoutes from 'routes'
import { store } from 'store/store'
import { setInterceptors } from 'utils/apiService/httpService'
import { permissions } from 'utils/genericData/getPermissions'
import {
  applyLiveDiscussionUnread,
  asyncConnectUser,
  asyncJoinOpenChannel,
  backfillSuperGroupUnread,
  groupChannelListenerName,
  openChannelListenerName,
  refreshUnreadDiscussions,
  scheduleUnreadDiscussionRefresh,
  sbChatClient,
} from 'utils/getChatClient'
import { showApiFailedSnackbar, showExceptionSnackbar } from 'utils/getSnackbarMessage'
import {
  clearPendoSession,
  getPendoVisitorId,
  initializePendo,
  isPendoEnabled,
  pendoUpdateInstance,
} from 'utils/pendoFunctions'

import { theme as defaultTheme } from './config/Theme'

const NOTIFICATION_RECHECK_DELAY_MS = 1500

const App = () => {
  usePendoPageTracking()

  const { getTheme } = useThemeService()
  const [permissionArray, setPermissionArray] = useState<number[]>([])
  const userId = Cookies.get('chat_user_id')
  const userSessionToken = Cookies.get('gcc_chat_token')
  const { setnewNotificationReceivedStatus } = useNotificationsService()
  const {
    getSelectedRoleLevel,
    getSelectedFacilities,
    loginInfo,
    getPermissionIds,
    getProfileData,
  } = useLoginService()
  const { getTodoList, setIsTaskListPending } = useReferralsService()
  const {
    setNewChatDetails,
    setDeletedChatDetails,
    setChatNotificationList,
    setUnreadChatThreadCount,
    setNewMemberDetails,
  } = useDiscussionService()
  const theme = useMemo(
    () => defaultTheme(getTheme === 'light' ? LightColors : DarkColors),
    [getTheme],
  )
  useEffect(() => {
    const token = Cookies.get('gcc_access_token')
    if (token) setInterceptors('application/json', token)
  }, [])

  const defaultFacList = () => {
    if (getSelectedRoleLevel === 2) {
      return getSelectedFacilities?.map(facItem => facItem?.toString())
    }
    return []
  }

  // The unread count is an inbox count, so it refreshes regardless of the user's discussion
  // notification preference, which only governs the notification feed.
  const updateDiscussionNotification = () => {
    scheduleUnreadDiscussionRefresh(
      setChatNotificationList,
      setUnreadChatThreadCount,
      defaultFacList(),
    )
  }

  const groupChannelHandler = new GroupChannelHandler({
    onMessageReceived: (channel, message) => {
      const chatBody = {
        channel,
        message,
      }
      setNewChatDetails(chatBody)
      applyLiveDiscussionUnread(channel as GroupChannel, setUnreadChatThreadCount)
      updateDiscussionNotification()
    },
    // An edit can add or remove a mention, and the backend re-indexes on the same event,
    // so the open discussion list is told to reconcile (18590).
    onMessageUpdated: (channel, message) => {
      setNewChatDetails({ channel, message })
    },
    onMessageDeleted: (channel, messageId) => {
      const chatBody = {
        channel,
        messageId,
      }
      setDeletedChatDetails(chatBody)
      applyLiveDiscussionUnread(channel as GroupChannel, setUnreadChatThreadCount)
      updateDiscussionNotification()
    },
    onUserJoined: (channel, user) => {
      const newUser = user as unknown as Member
      const memberBody = {
        channel,
        user: newUser,
      }
      setNewMemberDetails(memberBody)
    },
  })

  // The bell refetches its own count when this flag flips, so it no longer waits on (or
  // fails with) the legacy list fetch. The ping can land before the backend commits the
  // row, so flip once more shortly after to pick it up (20045).
  const refreshNotificationBadge = () => {
    setnewNotificationReceivedStatus()
    setTimeout(setnewNotificationReceivedStatus, NOTIFICATION_RECHECK_DELAY_MS)
  }

  const checkPendingTaskList = (requestBody: TaskListRequest) => {
    getTodoList(requestBody)
      .then(res => {
        if (res.data.isSuccess) {
          const taskList = res?.data as unknown as TodoListResponse
          if (taskList?.totalCount && taskList?.totalCount !== undefined) {
            setIsTaskListPending(true)
          }
        } else {
          showApiFailedSnackbar(res?.data?.message)
        }
      })
      .catch(error => {
        showExceptionSnackbar(error)
      })
  }

  const checkPermissions = () => {
    const acceptOrDenyPermissions = []

    if (
      getPermissionIds.includes(
        permissions?.filter(item => item.code === 'taskList-accept')?.[0]?.id,
      )
    ) {
      acceptOrDenyPermissions.push(
        permissions?.filter(item => item.code === 'taskList-accept')?.[0]?.id,
      )
    }

    if (
      getPermissionIds.includes(permissions?.filter(item => item.code === 'taskList-deny')?.[0]?.id)
    ) {
      acceptOrDenyPermissions.push(
        permissions?.filter(item => item.code === 'taskList-deny')?.[0]?.id,
      )
    }

    return acceptOrDenyPermissions
  }

  const defaultOrgList = () => {
    if (getSelectedRoleLevel === 1 || getSelectedRoleLevel === 2)
      return [loginInfo.organizationId as number]
    return []
  }

  useEffect(() => {
    const permissionArrayValues = checkPermissions()
    setPermissionArray(permissionArrayValues)
  }, [getPermissionIds])

  const getToDoListItems = () => {
    const body: TaskListRequest = {
      pageNumber: 1,
      pageSize: 10,
      sort: '',
      search: '',
      permissionList: permissionArray,
      taskListStatus: 1,
      filterParams: [
        {
          name: 'facilityIds',
          value: defaultFacList().map(item => item?.toString()),
        },
        {
          name: 'organizationId',
          value: defaultOrgList().map(item => item?.toString()),
        },
        { name: 'taskType', value: [] },
      ],
    }
    if (permissionArray?.length && permissionArray?.length !== undefined) {
      checkPendingTaskList(body)
    }
  }

  const openChannelHandler = new OpenChannelHandler({
    onMessageReceived: (channel, message) => {
      const user = Cookies.get('chat_user_id')

      if (user && message?.mentionedUserIds?.includes(user)) {
        if (channel?.url === Env.TASKLIST_CHANNEL_ID) {
          getToDoListItems()
          return
        }
        refreshNotificationBadge()
      }
    },
  })

  useEffect(() => {
    if (userId && userSessionToken && userId !== undefined && userSessionToken !== undefined) {
      asyncConnectUser(userId, userSessionToken).then(() => {
        asyncJoinOpenChannel()
        sbChatClient.groupChannel.addGroupChannelHandler(
          groupChannelListenerName,
          groupChannelHandler,
        )
        sbChatClient.openChannel.addOpenChannelHandler(openChannelListenerName, openChannelHandler)
        if (loginInfo?.userId) {
          refreshUnreadDiscussions(
            setChatNotificationList,
            setUnreadChatThreadCount,
            defaultFacList(),
          )
            .then(() => backfillSuperGroupUnread(defaultFacList(), setUnreadChatThreadCount))
            .catch(() => {
              // Keep last tracked badge; Super Group backfill is best-effort.
            })
        }
      })
    }
    return () => {
      // Release any resources or perform cleanup here
      sbChatClient.groupChannel.removeGroupChannelHandler(groupChannelListenerName)
      sbChatClient.openChannel.removeOpenChannelHandler(openChannelListenerName)
    }
  }, [userId, userSessionToken, getSelectedFacilities])

  useEffect(() => {
    if (
      permissionArray?.length &&
      permissionArray?.length !== undefined &&
      userSessionToken &&
      userSessionToken !== undefined
    ) {
      getToDoListItems()
    }
  }, [permissionArray, loginInfo.organizationId, userSessionToken])

  const getRole = (rolesString?: string) => {
    if (rolesString) {
      const roles: RoleObject[] = JSON.parse(rolesString)
      const currentRole = roles?.find(role => role.Level === getSelectedRoleLevel)
      return currentRole ? currentRole.Name : ''
    }
    return ''
  }

  useEffect(() => {
    if (loginInfo?.userId && getPendoVisitorId() !== loginInfo?.userId && isPendoEnabled()) {
      const updateBody = {
        id: loginInfo.userId,
        email: loginInfo.email ?? '',
        firstName: getProfileData.firstName ?? '',
        lastName: getProfileData.lastName ?? '',
        role: getRole(loginInfo.roles),
        orgId: loginInfo.organizationId ? String(loginInfo.organizationId) : null,
        orgName: loginInfo.organizationName,
      }
      clearPendoSession()
      setTimeout(() => {
        initializePendo(updateBody)
      }, 1000)
    }
  }, [loginInfo?.userId])

  useEffect(() => {
    if (getProfileData?.id && loginInfo.userId && isPendoEnabled()) {
      const updateBody = {
        id: loginInfo.userId,
        email: loginInfo.email ?? '',
        firstName: getProfileData.firstName ?? '',
        lastName: getProfileData.lastName ?? '',
        role: getRole(loginInfo.roles),
        orgId: loginInfo.organizationId ? String(loginInfo.organizationId) : null,
        orgName: loginInfo.organizationName,
      }

      pendoUpdateInstance(updateBody)
    }
  }, [getProfileData])

  return (
    <StyledEngineProvider injectFirst>
      <ThemeProvider theme={theme}>
        <SnackbarProvider
          anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
          autoHideDuration={1300}
          maxSnack={3}
          hideIconVariant={true}
          style={{
            whiteSpace: 'pre-line',
          }}
        >
          <SnackbarUtilsConfigurator />
          <AppRoutes />
        </SnackbarProvider>
      </ThemeProvider>
    </StyledEngineProvider>
  )
}

const WrapperApp = () => (
  <Provider store={store}>
    {/* <HistoryRouter history={history}> */}
    <BrowserRouter>
      <App />
    </BrowserRouter>
    {/* </HistoryRouter> */}
  </Provider>
)

export default WrapperApp
